import { fetchVoicemailsBatch } from '../../services/WebexVoicemailService';
import { t } from "i18next";
import { Observable, ReplaySubject, Subject, from, tap } from "rxjs";
import {
  ERROR_RESPONSE_TEXT,
  IVoiceMailDeleteResponse,
  IVoiceMailReadResponse,
  IVoiceMailResponse,
  IVoiceMailTranscriptMessage,
  IVoiceMessageContentResponse,
  IVoicemailAdapter,
  IWebexVoicemail,
  RESPONSE_STATUS,
  VM_STORAGE_KEYS,
  VOICEMAIL_LIMIT,
} from "../../../../adapter-interfaces/src";
import {
  removeLocalStorageItem
} from "../../../../logger/src";
import { Logger, formatError } from "../../../../logger/src/Logger";
import { submitMetrics } from "../../services/WebexMetricsService";
import {
  deleteVoiceMail,
  getVMTranscript,
  getVoiceMailList,
  fetchVoicemailsPaginated,
  getVoiceMessage,
  getWebexCallingBackendUser,
  readVoiceMessage,
  unreadVoiceMessage,
} from "../../services/WebexVoicemailService";
import { CustomError } from '../../utils/CustomError';
import { voiceMailDurationMetric, voicemail } from "../../utils/MetricUtils";
import { CALLING_BACKEND, getCallingBackEnd, setLocalStoragewithDispatchEvent } from "../../utils/callingUtils";
import { normalizeAddress } from '../../utils/normalizeAddress';

export class WebexVoicemailAdapter implements IVoicemailAdapter {
  // Infinite scroll state
  private _offset: number = 0;
  private _done: boolean = false;
  private _batchSize: number = VOICEMAIL_LIMIT.BATCH_SIZE;
  private _allVoicemails: IWebexVoicemail[] = [];
  private readStateOverrides = new Map<string, boolean>();
  private batchFetchInFlight: boolean = false;
  private refreshInFlight: boolean = false;
  private requestGeneration = 0;
  private batchFetchPromise: Promise<IWebexVoicemail[]> | null = null;
  private errors$ = new Subject<unknown>();
  // Fix 3b: dedupe `WEBEX_INSTANCE_TOKEN_EXPIRED` storm. A burst of
  // concurrent 401s from VoiceMessagingMessages would otherwise dispatch one
  // `storage` event per response, each one waking the LandingPage handler
  // and triggering another full SDK re-init + device registration.
  private tokenExpiryFlagged: boolean = false;

  private flagTokenExpiredOnce(context: string): void {
    if (this.tokenExpiryFlagged) {
      Logger.info(
        `WebexVoicemailAdapter.${context}: 401 already flagged, skipping duplicate token-expired dispatch.`
      );
      return;
    }
    this.tokenExpiryFlagged = true;
    setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.WEBEX_INSTANCE_TOKEN_EXPIRED, true);
  }

  private clearTokenExpiryFlag(): void {
    this.tokenExpiryFlagged = false;
  }

  private persistListErrorStatus(statusCode?: number): void {
    switch (statusCode) {
      case RESPONSE_STATUS.STATUSCODE_401:
        this.flagTokenExpiredOnce('fetchNextBatch');
        return;
      case RESPONSE_STATUS.STATUSCODE_400:
        if (window?.isWebexInitalized && getCallingBackEnd() === CALLING_BACKEND.WXC) {
          return;
        }
        break;
      case RESPONSE_STATUS.STATUSCODE_404:
        if (window?.isWebexInitalized && getCallingBackEnd() === CALLING_BACKEND.UCM) {
          return;
        }
        break;
      default:
        break;
    }
    setLocalStoragewithDispatchEvent(
      VM_STORAGE_KEYS.VM_ERROR_LIST_MSG,
      ERROR_RESPONSE_TEXT.ERROR_400_500
    );
    window.dispatchEvent(new Event('storage'));
  }

  /**
   * Fetches the next batch of voicemails for infinite scroll.
   * Emits the cumulative list after each batch.
   * Returns an Observable of the new cumulative list.
   */
  fetchNextBatch(): Observable<IWebexVoicemail[]> {
    Logger.info(`[WebexVoicemailAdapter] fetchNextBatch called. _offset=${this._offset}, _done=${this._done}, _batchSize=${this._batchSize}, _allVoicemails.length=${this._allVoicemails.length}`);
    if (!this.observables$) {
      Logger.info('[WebexVoicemailAdapter] Initializing observables$ in fetchNextBatch');
      this.observables$ = new ReplaySubject<IWebexVoicemail[]>();
    }
    if (this._done) {
      Logger.info('[WebexVoicemailAdapter] fetchNextBatch: _done is true, emitting current voicemails and returning');
      this.observables$.next([...this._allVoicemails]);
      return this.observables$;
    }
    if (this.batchFetchInFlight || this.refreshInFlight) {
      Logger.info('[WebexVoicemailAdapter] fetchNextBatch: request already in flight, returning shared observable');
      return this.observables$;
    }
    this.batchFetchInFlight = true;
    const requestGeneration = this.requestGeneration;
    const requestOffset = this._offset;
    Logger.info(`[WebexVoicemailAdapter] Fetching batch: offset=${this._offset}, batchSize=${this._batchSize}`);
    const completionPromise = new Promise<IWebexVoicemail[]>((resolve, reject) => {
      from(fetchVoicemailsBatch(requestOffset, this._batchSize)).subscribe({
        next: (batch: IVoiceMailResponse) => {
          if (requestGeneration !== this.requestGeneration) {
            resolve([...this._allVoicemails]);
            return;
          }
          Logger.info(`[WebexVoicemailAdapter] fetchVoicemailsBatch returned. offset=${requestOffset}, batchSize=${this._batchSize}, batch.data.voicemailList.length=${batch?.data?.voicemailList?.length}`);
          const isSuccessfulResponse =
            batch?.statusCode === RESPONSE_STATUS.STATUSCODE_200 ||
            batch?.statusCode === RESPONSE_STATUS.STATUSCODE_204;
          if (!isSuccessfulResponse) {
            this.batchFetchInFlight = false;
            const error = new CustomError(
              batch?.message || 'Failed to fetch voicemails',
              batch?.statusCode ?? -1
            );
            this.persistListErrorStatus(batch?.statusCode);
            Logger.error(
              `[WebexVoicemailAdapter] fetchVoicemailsBatch returned failure: ${formatError(error)}`
            );
            this.errors$.next(error);
            reject(error);
            return;
          }
          const rawBatchLength = batch?.data?.voicemailList?.length ?? 0;
          const parsed = this.parseVoicemailData(batch);
          Logger.info(`[WebexVoicemailAdapter] Parsed batch length: ${parsed.length}`);
          if (rawBatchLength < this._batchSize) {
            Logger.info('[WebexVoicemailAdapter] Setting _done=true (end of list or partial batch)');
            this._done = true;
          }
          this._offset += rawBatchLength;
          Logger.info(`[WebexVoicemailAdapter] Updated _offset: ${this._offset}`);
          this._allVoicemails = this.applyReadStateOverrides(
            this._allVoicemails.concat(parsed)
          );
          Logger.info(`[WebexVoicemailAdapter] _allVoicemails.length after concat: ${this._allVoicemails.length}`);
          this.observables$.next([...this._allVoicemails]);
          this.batchFetchInFlight = false;
          resolve([...this._allVoicemails]);
        },
        error: (err) => {
          if (requestGeneration !== this.requestGeneration) {
            resolve([...this._allVoicemails]);
            return;
          }
          this.batchFetchInFlight = false;
          this.persistListErrorStatus();
          Logger.error(`[WebexVoicemailAdapter] fetchVoicemailsBatch error: ${formatError(err)}`);
          this.errors$.next(err);
          reject(err);
        }
      });
    });
    this.batchFetchPromise = completionPromise;
    completionPromise.then(
      () => {
        if (this.batchFetchPromise === completionPromise) this.batchFetchPromise = null;
      },
      () => {
        if (this.batchFetchPromise === completionPromise) this.batchFetchPromise = null;
      }
    );
    return this.observables$;
  }

  /** Resolves when the requested page has completed, for mobile scroll guards. */
  async fetchNextBatchAsync(): Promise<IWebexVoicemail[]> {
    this.fetchNextBatch();
    return this.batchFetchPromise ?? [...this._allVoicemails];
  }

  /**
   * Resets the infinite scroll state (for refresh or reload)
   */
  resetInfiniteScroll() {
    Logger.info('[WebexVoicemailAdapter] resetInfiniteScroll called. Resetting _offset, _done, _allVoicemails');
    this._offset = 0;
    this._done = false;
    this._allVoicemails = [];
    this.readStateOverrides.clear();
    this.batchFetchInFlight = false;
    this.refreshInFlight = false;
    this.requestGeneration += 1;
    this.clearTokenExpiryFlag();
    // Do not replay data from the previous account after an identity change.
    // Existing requests are invalidated by requestGeneration above; callers
    // subscribe to this fresh subject after the reset.
    this.observables$ = new ReplaySubject<IWebexVoicemail[]>();
  }
  observables$!: ReplaySubject<IWebexVoicemail[]>;

  getErrors(): Observable<unknown> {
    return this.errors$.asObservable();
  }

  hasMoreVoicemails(): boolean {
    return !this._done;
  }

  parseVoicemailData(response: IVoiceMailResponse): IWebexVoicemail[] {
    let output: IWebexVoicemail[] = [];
    output = (response?.data?.voicemailList ?? []).filter(item => item?.duration?.$).map((item) => {
      const voiceMailResponseData: IWebexVoicemail = {
        id: item?.messageId?.$,
        name: item?.callingPartyInfo?.name?.$ ? item?.callingPartyInfo?.name?.$ : (!item?.callingPartyInfo?.name?.$ && !item?.callingPartyInfo?.address?.$) ? t("calls.call_type_unknown") : '',
        address: normalizeAddress(item?.callingPartyInfo?.address?.$),
        date: new Date(Number(item?.time?.$)).toISOString(),
        unread: item?.read ? false : true,
        duration: item?.duration?.$ ? Number(item?.duration?.$) : 0,
      };
      submitMetrics(
        {
          metricEvent: voiceMailDurationMetric,
          client_timestamp: voicemail,
          trackingId: voicemail,
          key: voicemail,
          messageId: voiceMailResponseData?.id,
          voiceMailDuration: `${(voiceMailResponseData?.duration)/1000}`,
        }
      );
      return voiceMailResponseData;
    });
    return this.applyReadStateOverrides(output, true);
  }

  private applyReadStateOverrides(
    voicemails: IWebexVoicemail[],
    retireWhenMatched = false
  ): IWebexVoicemail[] {
    if (this.readStateOverrides.size === 0) {
      return voicemails;
    }

    return voicemails.map((voicemail) => {
      const unread = this.readStateOverrides.get(voicemail.id);
      if (unread === undefined) {
        return voicemail;
      }
      if (voicemail.unread === unread) {
        if (retireWhenMatched) {
          this.readStateOverrides.delete(voicemail.id);
        }
        return voicemail;
      }
      return { ...voicemail, unread };
    });
  }

  private isReadMutationSuccess(response?: IVoiceMailReadResponse) {
    return Boolean(
      response &&
        (response.message === RESPONSE_STATUS.SUCCESS ||
          response.statusCode === RESPONSE_STATUS.STATUSCODE_200 ||
          response.statusCode === RESPONSE_STATUS.STATUSCODE_204)
    );
  }

  // Revert: emit the full, cumulative array after every batch (old behavior)
  /**
   * Refreshes the voicemail list.
   * @param isAutoRefresh If true, only fetches new voicemails since the latest known. If false (default), resets and fetches first batch.
   */
  async refresh(isAutoRefresh: boolean = false) {
    if (!this.observables$) {
      this.observables$ = new ReplaySubject<IWebexVoicemail[]>();
    }
    if (this.refreshInFlight) {
      Logger.info('[WebexVoicemailAdapter] refresh skipped because another refresh is in flight');
      return;
    }
    const requestGeneration = ++this.requestGeneration;
    this.refreshInFlight = true;
    this.batchFetchInFlight = false;
    if (!isAutoRefresh) {
      // Initial load or manual refresh: reset state and fetch first batch
      this._offset = 0;
      this._done = false;
      this._allVoicemails = [];
      try {
        await fetchVoicemailsPaginated(0, this._batchSize, (batch: IVoiceMailResponse, isDone: boolean) => {
          if (requestGeneration !== this.requestGeneration) return;
          Logger.info(
            `WebexVoicemailAdapter.refresh(): got batch with message: ${batch?.statusCode} - ${batch?.message}`
          );
          if (!batch) {
            Logger.error('WebexVoicemailAdapter.refresh(): batch is undefined or null');
            setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, 'Batch is undefined');
            window.dispatchEvent(new Event('storage'));
            this.errors$.next(new CustomError('Batch is undefined', -1));
            return;
          }
          if (batch.statusCode === RESPONSE_STATUS.STATUSCODE_200 || batch.statusCode === RESPONSE_STATUS.STATUSCODE_204) {
            removeLocalStorageItem(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG);
            window.dispatchEvent(new Event("storage"));
            this.clearTokenExpiryFlag();
            const parsed = this.parseVoicemailData(batch);
            this._allVoicemails = this.applyReadStateOverrides(parsed);
            const rawBatchLength = batch?.data?.voicemailList?.length ?? 0;
            this._offset = rawBatchLength;
            this._done = rawBatchLength < this._batchSize;
            this.observables$.next([...this._allVoicemails]);
            Logger.info('WebexVoicemailAdapter.refresh(): batch success, voicemails emitted');
          } else {
            if (batch.message === RESPONSE_STATUS.FAILURE) {
              Logger.error(`WebexVoicemailAdapter.refresh(): batch failure, statusCode=${batch.statusCode}`);
              switch (batch.statusCode) {
                case RESPONSE_STATUS.STATUSCODE_401:
                  Logger.error('WebexVoicemailAdapter.refresh(): 401 - Token expired');
                  this.flagTokenExpiredOnce('refresh()');
                  break;
                case RESPONSE_STATUS.STATUSCODE_400:
                  Logger.error('WebexVoicemailAdapter.refresh(): 400 - Bad request');
                  if (window?.isWebexInitalized && getCallingBackEnd() === CALLING_BACKEND.WXC) {
                    Logger.info(
                      `WebexVoicemailAdapter.refresh():Voicemail is not configured for WebexCalling User`
                    );
                  } else {
                    Logger.error('WebexVoicemailAdapter.refresh(): 400 - Setting error banner');
                    setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  }
                  break;
                case RESPONSE_STATUS.STATUSCODE_404:
                  Logger.error('WebexVoicemailAdapter.refresh(): 404 - Not found');
                  if (window?.isWebexInitalized && getCallingBackEnd() === CALLING_BACKEND.UCM) {
                    Logger.info(
                      `WebexVoicemailAdapter.refresh():Voicemail is not configured for UCMC User`
                    );
                  } else {
                    Logger.error('WebexVoicemailAdapter.refresh(): 404 - Setting error banner');
                    setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  }
                  break;
                case RESPONSE_STATUS.STATUSCODE_403:
                  Logger.error('WebexVoicemailAdapter.refresh(): 403 - Forbidden');
                  setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  break;
                case RESPONSE_STATUS.STATUSCODE_408:
                  Logger.error('WebexVoicemailAdapter.refresh(): 408 - Request Timeout');
                  setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  break;
                case RESPONSE_STATUS.STATUSCODE_422:
                  Logger.error('WebexVoicemailAdapter.refresh(): 422 - Unprocessable Entity');
                  setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  break;
                case RESPONSE_STATUS.STATUSCODE_500:
                  Logger.error('WebexVoicemailAdapter.refresh(): 500 - Internal Server Error');
                  setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  break;
                case RESPONSE_STATUS.STATUSCODE_503:
                  Logger.error('WebexVoicemailAdapter.refresh(): 503 - Service Unavailable');
                  setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  break;
                default:
                  Logger.error(`WebexVoicemailAdapter.refresh(): Unhandled error status code: ${batch?.statusCode}`);
                  Logger.info(
                    `WebexVoicemailAdapter.refresh(): Unhandled status code: ${batch?.statusCode} - ${batch?.message}`
                  );
                  break;
              }
              window.dispatchEvent(new Event('storage'));
            }
            this.errors$.next(new CustomError(batch.message, batch.statusCode));
          }
        });
      } catch (err) {
        Logger.error(`WebexVoicemailAdapter.refresh(): got error: ${formatError(err)}`);
        if (requestGeneration === this.requestGeneration) {
          this.errors$.next(new CustomError(formatError(err), -1));
        }
      } finally {
        if (requestGeneration === this.requestGeneration) {
          this.refreshInFlight = false;
        }
      }
    } else {
      // Auto-refresh: fetch only new voicemails since the latest known
      const latestId = this._allVoicemails.length > 0 ? this._allVoicemails[0].id : undefined;
      try {
        // For demonstration, we fetch the first batch and filter for new IDs
        await fetchVoicemailsPaginated(0, this._batchSize, (batch: IVoiceMailResponse, isDone: boolean) => {
          if (requestGeneration !== this.requestGeneration) return;
          Logger.info(
            `WebexVoicemailAdapter.refresh(auto): got batch with message: ${batch?.statusCode} - ${batch?.message}`
          );
          if (!batch) {
            Logger.error('WebexVoicemailAdapter.refresh(auto): batch is undefined or null');
            setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, 'Batch is undefined');
            window.dispatchEvent(new Event('storage'));
            this.errors$.next(new CustomError('Batch is undefined', -1));
            return;
          }
          if (batch.statusCode === RESPONSE_STATUS.STATUSCODE_200 || batch.statusCode === RESPONSE_STATUS.STATUSCODE_204) {
            removeLocalStorageItem(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG);
            window.dispatchEvent(new Event("storage"));
            this.clearTokenExpiryFlag();
            const parsed = this.parseVoicemailData(batch);
            // Only prepend new voicemails (not already in _allVoicemails)
            const newVoicemails = latestId ? parsed.filter(vm => vm.id !== latestId && !this._allVoicemails.some(existing => existing.id === vm.id)) : parsed;
            if (newVoicemails.length > 0) {
              const rawVoicemails = batch?.data?.voicemailList ?? [];
              const indexOfLatest = latestId
                ? rawVoicemails.findIndex((item) => item?.messageId?.$ === latestId)
                : -1;
              const newRawRecordCount = latestId
                ? (indexOfLatest >= 0 ? indexOfLatest : rawVoicemails.length)
                : rawVoicemails.length;
              this._allVoicemails = this.applyReadStateOverrides(
                newVoicemails.concat(this._allVoicemails)
              );
              this._offset += newRawRecordCount;
              this.observables$.next([...this._allVoicemails]);
              Logger.info('WebexVoicemailAdapter.refresh(auto): new voicemails emitted');
            } else {
              Logger.info('WebexVoicemailAdapter.refresh(auto): no new voicemails');
            }
          } else {
            if (batch.message === RESPONSE_STATUS.FAILURE) {
              Logger.error(`WebexVoicemailAdapter.refresh(auto): batch failure, statusCode=${batch.statusCode}`);
              // ...same error handling as above...
              switch (batch.statusCode) {
                case RESPONSE_STATUS.STATUSCODE_401:
                  Logger.error('WebexVoicemailAdapter.refresh(auto): 401 - Token expired');
                  this.flagTokenExpiredOnce('refresh(auto)');
                  break;
                case RESPONSE_STATUS.STATUSCODE_400:
                  Logger.error('WebexVoicemailAdapter.refresh(auto): 400 - Bad request');
                  if (window?.isWebexInitalized && getCallingBackEnd() === CALLING_BACKEND.WXC) {
                    Logger.info(
                      `WebexVoicemailAdapter.refresh(auto):Voicemail is not configured for WebexCalling User`
                    );
                  } else {
                    Logger.error('WebexVoicemailAdapter.refresh(auto): 400 - Setting error banner');
                    setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  }
                  break;
                case RESPONSE_STATUS.STATUSCODE_404:
                  Logger.error('WebexVoicemailAdapter.refresh(auto): 404 - Not found');
                  if (window?.isWebexInitalized && getCallingBackEnd() === CALLING_BACKEND.UCM) {
                    Logger.info(
                      `WebexVoicemailAdapter.refresh(auto):Voicemail is not configured for UCMC User`
                    );
                  } else {
                    Logger.error('WebexVoicemailAdapter.refresh(auto): 404 - Setting error banner');
                    setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  }
                  break;
                case RESPONSE_STATUS.STATUSCODE_403:
                  Logger.error('WebexVoicemailAdapter.refresh(auto): 403 - Forbidden');
                  setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  break;
                case RESPONSE_STATUS.STATUSCODE_408:
                  Logger.error('WebexVoicemailAdapter.refresh(auto): 408 - Request Timeout');
                  setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  break;
                case RESPONSE_STATUS.STATUSCODE_422:
                  Logger.error('WebexVoicemailAdapter.refresh(auto): 422 - Unprocessable Entity');
                  setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  break;
                case RESPONSE_STATUS.STATUSCODE_500:
                  Logger.error('WebexVoicemailAdapter.refresh(auto): 500 - Internal Server Error');
                  setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  break;
                case RESPONSE_STATUS.STATUSCODE_503:
                  Logger.error('WebexVoicemailAdapter.refresh(auto): 503 - Service Unavailable');
                  setLocalStoragewithDispatchEvent(VM_STORAGE_KEYS.VM_ERROR_LIST_MSG, ERROR_RESPONSE_TEXT.ERROR_400_500);
                  break;
                default:
                  Logger.error(`WebexVoicemailAdapter.refresh(auto): Unhandled error status code: ${batch?.statusCode}`);
                  Logger.info(
                    `WebexVoicemailAdapter.refresh(auto): Unhandled status code: ${batch?.statusCode} - ${batch?.message}`
                  );
                  break;
              }
              window.dispatchEvent(new Event('storage'));
            }
            this.errors$.next(new CustomError(batch.message, batch.statusCode));
          }
        });
      } catch (err) {
        Logger.error(`WebexVoicemailAdapter.refresh(auto): got error: ${formatError(err)}`);
        if (requestGeneration === this.requestGeneration) {
          this.errors$.next(new CustomError(formatError(err), -1));
        }
      } finally {
        if (requestGeneration === this.requestGeneration) {
          this.refreshInFlight = false;
        }
      }
    }
  }

  getAll(): Observable<IWebexVoicemail[]> {
    if (!this.observables$) {
      this.observables$ = new ReplaySubject<IWebexVoicemail[]>();
    }
    return this.observables$;
  }

  deleteVoicemail(ID: string): Observable<IVoiceMailDeleteResponse> {
    return from(deleteVoiceMail(ID)).pipe(
      tap((response) => {
        if (response?.message !== RESPONSE_STATUS.SUCCESS) {
          return;
        }
        this._allVoicemails = this._allVoicemails.filter(
          (voicemail) => voicemail.id !== ID
        );
        this.observables$?.next([...this._allVoicemails]);
      })
    );
  }

  private patchVoicemailReadState(ID: string, unread: boolean) {
    this.readStateOverrides.set(ID, unread);
    this._allVoicemails = this._allVoicemails.map((voicemail) =>
      voicemail.id === ID ? { ...voicemail, unread } : voicemail
    );
    this.observables$?.next([...this._allVoicemails]);
  }

  markVoicemailRead(ID: string): Observable<IVoiceMailReadResponse> {
    return from(readVoiceMessage(ID)).pipe(
      tap((response) => {
        if (this.isReadMutationSuccess(response)) {
          this.patchVoicemailReadState(ID, false);
        }
      })
    );
  }

  markVoicemailUnread(ID: string): Observable<IVoiceMailReadResponse> {
    return from(unreadVoiceMessage(ID)).pipe(
      tap((response) => {
        if (this.isReadMutationSuccess(response)) {
          this.patchVoicemailReadState(ID, true);
        }
      })
    );
  }

  getVoiceMessage(ID: string): Observable<IVoiceMessageContentResponse> {
    return from(getVoiceMessage(ID));
  }

  getVMTranscript(ID: string): Observable<IVoiceMailTranscriptMessage> {
    return from(getVMTranscript(ID));
  }

  getWebexCallingBackend(): Observable<boolean> {
        return from(getWebexCallingBackendUser());
  }
}
